import React, { useState } from 'react';
import { useChats } from '../../../hooks/useChats';
import ConversationPreview from './ConversationPreview';
import { v4 as uuidv4 } from "uuid";
import { Empty, Typography, Input, Tabs, Badge } from 'antd';
import { SearchOutlined, InboxOutlined, SendOutlined, EditOutlined } from '@ant-design/icons';
import { auth } from '../../../firebaseConfig';

const { Title, Text } = Typography;

const MessageInbox = () => {
  const chats = useChats();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('inbox');
  
  const currentUserId = auth.currentUser?.uid;

  // Filter chats based on the active tab
  const getFilteredChats = () => {
    if (!chats) return [];

    let filtered = chats;

    // Apply search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter(chat => {
        const userData = chat[1].userInfo;
        const searchLower = searchTerm.toLowerCase();
        return (
          userData.firstName?.toLowerCase().includes(searchLower) ||
          userData.lastName?.toLowerCase().includes(searchLower) ||
          chat[1].lastMessage?.text?.toLowerCase().includes(searchLower)
        );
      });
    }

    // Apply tab filter
    switch (activeTab) {
      case 'inbox':
        // Messages where the last message was NOT sent by current user
        return filtered.filter(chat => 
          chat[1].lastMessage && chat[1].lastMessage.senderID !== currentUserId
        );
      
      case 'sent':
        // Messages where the last message WAS sent by current user
        return filtered.filter(chat => 
          chat[1].lastMessage && chat[1].lastMessage.senderID === currentUserId
        );
      
      case 'drafts':
        // For now, return empty - you'd need to implement draft saving
        return [];
      
      default:
        return filtered;
    }
  };

  const filteredChats = getFilteredChats();
  const inboxChats = chats?.filter(chat => 
    chat[1].lastMessage && chat[1].lastMessage.senderID !== currentUserId
  ) || [];
  const sentChats = chats?.filter(chat => 
    chat[1].lastMessage && chat[1].lastMessage.senderID === currentUserId
  ) || [];
  
  const unreadCount = inboxChats.filter(chat => chat[1].unread).length;

  const getEmptyStateMessage = () => {
    switch (activeTab) {
      case 'inbox':
        return {
          title: 'No messages received',
          description: 'When people send you messages, they will appear here'
        };
      case 'sent':
        return {
          title: 'No messages sent',
          description: 'Messages you send will appear here'
        };
      case 'drafts':
        return {
          title: 'No drafts saved',
          description: 'Your saved message drafts will appear here'
        };
      default:
        return {
          title: 'No conversations',
          description: 'Start meaningful conversations with your matches'
        };
    }
  };

  return (
    <div style={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      background: 'rgba(255, 255, 255, 0.98)'
    }}>
      
      {/* Header */}
      <div style={{
        padding: '24px 20px 20px 20px',
        borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 249, 255, 0.95) 100%)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <Title level={3} style={{ 
            margin: 0, 
            color: '#2c3e50',
            fontWeight: '600'
          }}>
            Messages
          </Title>
          <Badge count={unreadCount} style={{ backgroundColor: '#667eea' }}>
            <InboxOutlined style={{ fontSize: '24px', color: '#64748b' }} />
          </Badge>
        </div>
        
        {/* Search */}
        <Input
          placeholder="Search conversations..."
          prefix={<SearchOutlined style={{ color: '#64748b' }} />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            borderRadius: '12px',
            background: 'rgba(255, 255, 255, 0.9)',
            border: '1px solid rgba(102, 126, 234, 0.2)'
          }}
        />
      </div>

      {/* Tabs for Inbox/Sent/Drafts */}
      <Tabs 
        activeKey={activeTab} 
        onChange={setActiveTab}
        style={{ padding: '0 20px' }}
        items={[
          {
            key: 'inbox',
            label: (
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <InboxOutlined />
                Inbox
                {unreadCount > 0 && <Badge count={unreadCount} size="small" />}
              </span>
            )
          },
          {
            key: 'sent',
            label: (
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <SendOutlined />
                Sent
                <Badge count={sentChats.length} size="small" showZero={false} />
              </span>
            )
          },
          {
            key: 'drafts',
            label: (
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <EditOutlined />
                Drafts
                <Badge count={0} size="small" showZero={false} />
              </span>
            )
          }
        ]}
      />

      {/* Message List */}
      <div style={{ 
        flex: 1, 
        overflow: 'auto',
        padding: '0 0 20px 0'
      }}>
        {filteredChats?.length === 0 ? (
          <div style={{ 
            padding: '60px 20px', 
            textAlign: 'center' 
          }}>
            <Empty
              imageStyle={{ height: 80 }}
              description={
                <div>
                  <Text style={{ color: '#64748b', fontSize: '16px', display: 'block', marginBottom: '8px' }}>
                    {getEmptyStateMessage().title}
                  </Text>
                  <Text style={{ color: '#94a3b8', fontSize: '14px' }}>
                    {getEmptyStateMessage().description}
                  </Text>
                </div>
              }
            />
          </div>
        ) : (
          filteredChats
            ?.sort((a, b) => b[1].date - a[1].date)
            .map((chat) => (
              <ConversationPreview 
                key={uuidv4()} 
                conversation={chat} 
                activeTab={activeTab}
              />
            ))
        )}
      </div>
    </div>
  );
};

export default MessageInbox;